// Money formatting + parsing for the web SPA.
//
// READ THE UNITS RULE FIRST (CLAUDE.md): every *Cents value is an
// INTEGER number of MINOR UNITS OF ITS OWN CURRENCY — $152.34 USD is
// 15234, ¥48,000 JPY is 48000 (JPY has no minor unit). Nothing here may
// assume 100 minor units per major one; the scale always comes from
// `currencyExponent(code)` in the shared kernel.
//
// RENDERING IS NOT FORKED. formatMoney lives in @core/ledger-core so the
// Worker's balanceLine, the web, and mobile emit byte-identical strings;
// this module only re-exports it and adds the two web-only jobs the
// kernel has no business knowing about: turning minor units into an
// EDITABLE input string, and parsing a typed one back.

import {
  BASE_CURRENCY,
  currencyExponent,
  formatMoney,
  normalizeCurrencyCode,
  SUPPORTED_CURRENCIES,
} from '@core/ledger-core';

export { BASE_CURRENCY, SUPPORTED_CURRENCIES, currencyExponent, formatMoney };

/**
 * Render integer minor units in `code`. Thin alias over the kernel —
 * imported from here so a screen never has to reach past money.ts.
 *
 *   fmtMoney(15234, 'USD') → "$152.34"
 *   fmtMoney(48000, 'JPY') → "¥48,000"
 */
export function fmtMoney(minor: number, code: string): string {
  return formatMoney(minor, code);
}

/** Signed rendering for ledger rows: +$12.00 / −$12.00 (typographic minus). */
export function fmtSignedMoney(minor: number, code: string): string {
  if (minor === 0) return fmtMoney(0, code);
  return `${minor > 0 ? '+' : '−'}${fmtMoney(Math.abs(minor), code)}`;
}

/**
 * The currency's prefix as formatMoney writes it ("$", "¥", "CHF ",
 * "MX$"). Derived from formatMoney(0, code) rather than a second symbol
 * table, so the field's prefix can never disagree with the rendered
 * amount next to it.
 */
export function moneySymbol(code: string): string {
  return formatMoney(0, code).replace(/[\d.,-].*$/, '');
}

/**
 * Minor units → the EDITABLE string for an amount field. Currency-aware
 * and grouping-free (a grouped "48,000" in an input fights the caret):
 *
 *   minorToInput(7617,  'USD') → "76.17"
 *   minorToInput(48000, 'JPY') → "48000"
 *
 * Integer math — no float division, so a large amount can't drift.
 */
export function minorToInput(minor: number, code: string): string {
  const exponent = currencyExponent(code);
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(Math.round(minor));
  if (exponent === 0) return `${sign}${abs}`;
  const scale = 10 ** exponent;
  const whole = Math.floor(abs / scale);
  const frac = String(abs % scale).padStart(exponent, '0');
  return `${sign}${whole}.${frac}`;
}

/** The placeholder a field of this currency should show ("0.00" / "0"). */
export function amountPlaceholder(code: string): string {
  const exponent = currencyExponent(code);
  return exponent === 0 ? '0' : `0.${'0'.repeat(exponent)}`;
}

/**
 * Typed amount string → integer MINOR UNITS of `code`, or null when
 * unparseable.
 *
 * Accepts "15", "15.2", "15.23", ".50", "$15.23", "1,234.56", and
 * comma-decimal locale input "15,23" / "1.234,56" (a comma-decimal
 * keyboard offers a comma as the ONLY separator key in de/fr/es/…
 * regions — treating every comma as grouping inflated amounts 100×,
 * fixed in M4.2 and preserved here).
 *
 * CURRENCY-AWARE, which is the whole point: the scale is
 * 10^currencyExponent(code), never a hardcoded 100. For a ZERO-DECIMAL
 * currency (JPY/KRW) a decimal fraction is REJECTED rather than rounded
 * — "¥300.50" is not a thing, and silently flooring it to ¥300 or
 * scaling it to ¥30,050 are both wrong in ways the user can't see.
 *
 *   parseAmountToMinor("152.34", 'USD') → 15234
 *   parseAmountToMinor("48000",  'JPY') → 48000
 *   parseAmountToMinor("48,000", 'JPY') → 48000
 *   parseAmountToMinor("300.50", 'JPY') → null
 *
 * Integer math end-to-end: whole * 10^exp + fraction digits, never
 * Number(s) * 100.
 */
export function parseAmountToMinor(input: string, code: string): number | null {
  const exponent = currencyExponent(code);
  const s = normalizeSeparators(input);
  if (s === null) return null;
  const m =
    exponent === 0
      ? s.match(/^(\d{1,12})$/)
      : s.match(new RegExp(`^(\\d{0,12})(?:\\.(\\d{1,${exponent}}))?$`));
  if (!m) return null;
  const whole = m[1];
  const frac = exponent === 0 ? undefined : m[2];
  if (!whole && !frac) return null;
  const scale = 10 ** exponent;
  const major = whole ? parseInt(whole, 10) : 0;
  const minor = frac ? parseInt(frac.padEnd(exponent, '0'), 10) : 0;
  return major * scale + minor;
}

/**
 * Suggest a base-currency amount for `minor` units of `from`, given an
 * FX `rate` expressed per MAJOR unit (the /api/fx contract: JPY→USD ≈
 * 0.0065). Both exponents are needed — the rate says nothing about
 * minor units — so this is:
 *
 *   base_minor = minor / 10^exp(from) * rate * 10^exp(base)
 *
 * Returns null for a non-finite/non-positive rate or amount. This is a
 * SUGGESTION for a human to confirm; the ledger stores whatever they
 * accept, and no write ever depends on it (see /api/fx's header).
 */
export function suggestBaseMinor(
  minor: number,
  from: string,
  rate: number,
  baseCode: string = BASE_CURRENCY,
): number | null {
  if (!Number.isFinite(rate) || rate <= 0) return null;
  if (!Number.isFinite(minor) || minor <= 0) return null;
  const fromScale = 10 ** currencyExponent(from);
  const baseScale = 10 ** currencyExponent(baseCode);
  const out = Math.round((minor / fromScale) * rate * baseScale);
  return out > 0 ? out : null;
}

/** Uppercase a code, falling back to the base currency when unsupported. */
export function safeCurrency(code: unknown, fallback: string = BASE_CURRENCY): string {
  return normalizeCurrencyCode(code) ?? normalizeCurrencyCode(fallback) ?? BASE_CURRENCY;
}

// Collapse thousands/decimal separators to a canonical "digits[.digits]"
// so a single amount parser serves both period-decimal (US) and
// comma-decimal (EU) input. Rule: when BOTH separators appear, the one
// occurring LAST is the decimal and the other is grouping; when only
// commas appear, a single comma with 1–2 trailing digits is a decimal
// (15,23 → 15.23), otherwise commas are grouping (1,234 → 1234). Returns
// null on empty; malformed leftovers are caught by the caller's regex.
//
// Currency symbols and whitespace are stripped so "¥48,000" and
// "CHF 40.00" paste cleanly. Note what is deliberately NOT stripped: a
// minus sign. Amounts are positive by definition, and a "-5" that
// silently became 5 would be a worse answer than the null the caller's
// regex produces.
function normalizeSeparators(input: string): string | null {
  let s = input.trim().replace(/[\sA-Za-z$€£¥₩฿]/g, '');
  if (s === '') return null;
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  if (lastComma !== -1 && lastDot !== -1) {
    s = lastComma > lastDot
      ? s.replace(/\./g, '').replace(/,/g, '.') // EU: . grouping, , decimal
      : s.replace(/,/g, ''); // US: , grouping, . decimal
  } else if (lastComma !== -1) {
    const digitsAfter = s.length - lastComma - 1;
    const single = (s.match(/,/g) || []).length === 1;
    s = single && (digitsAfter === 1 || digitsAfter === 2)
      ? s.replace(',', '.') // comma-decimal: 15,23 → 15.23
      : s.replace(/,/g, ''); // grouping: 1,234 → 1234
  }
  return s;
}
